import { ecorEnv } from '@/../environments/environment';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventEnum, ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, Inject, OnDestroy, OnInit, signal, computed, inject, DestroyRef } from '@angular/core';
import { Validators, ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequestExtensionDialogData } from '../../dialogs-data/request-extension-dialog-data';
import { RequestExtensionDialogHttpService } from '../../services/request-extension-dialog-http.service';
import { CalendarDialogComponent } from '../calendar-dialog/calendar-dialog.component';
import { CalendarDialogHttpService } from '../../services/calendar-dialog-http.service';
import { ExportSurveyDialogService } from '../../services/export-survey-dialog.service';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { NgxMaskModule } from 'ngx-mask';

@Component({
    selector: 'app-request-extension-dialog',
    templateUrl: './request-extension-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        NgxMaskModule
    ],
    providers: [
        ParadataEventService,
        RequestExtensionDialogHttpService,
        ExportSurveyDialogService,
        CalendarDialogHttpService
    ]
})
export class RequestExtensionDialogComponent extends CalendarDialogComponent implements OnInit, OnDestroy {
    protected destroyRef = inject(DestroyRef);
    private userStore = inject(Store<PortalUserState>);
    private requestParadataEventService = inject(ParadataEventService);
    private requestExtensionDialogHttpService = inject(RequestExtensionDialogHttpService);
    private requestExportSurveyDialogService = inject(ExportSurveyDialogService);
    private matDialogRef = inject(MatDialogRef<RequestExtensionDialogComponent>);
    private requestCalendarDialogHttpService = inject(CalendarDialogHttpService);

    public requestExtensionDialogData = inject<RequestExtensionDialogData>(MAT_DIALOG_DATA);

    // Signals for reactive state
    private requestUser = signal<PortalUser | null>(null);
    private requestExtensionError = signal<string>('');
    private success = signal<boolean>(false);
    private updateMessage = signal<string>('');
    private requestExtensionButtonDisabled = signal<boolean>(false);
    private hideHowDoIMsg = signal<boolean>(true);
    private surveyAdded = signal<string>('');
    private successMessage = signal<string>('');
    private extensionMessageDate = signal<Date | null>(null);
    private survey = signal<SurveyCard | null>(null);
    private newDueDate = signal<Date | null>(null);
    private mostRecentDueDate = signal<Date | null>(null);
    extendedDueDate = signal<Date | null>(null);
    private minExtendedDueDate = signal<Date | null>(null);
    private maxExtendedDueDate = signal<Date | null>(null);
    private originalDueDate = signal<Date | null>(null);
    private endDate = signal<Date | null>(null);
    private extensionError = signal<string>('');
    private today = signal<Date | null>(null);
    private dateError = signal<string>('');

    // Computed properties
    currentUser = computed(() => this.requestUser());
    currentRequestExtensionError = computed(() => this.requestExtensionError());
    isSuccess = computed(() => this.success());
    currentUpdateMessage = computed(() => this.updateMessage());
    isRequestExtensionButtonDisabled = computed(() => this.requestExtensionButtonDisabled());
    isHideHowDoIMsg = computed(() => this.hideHowDoIMsg());
    currentSurveyAdded = computed(() => this.surveyAdded());
    currentSuccessMessage = computed(() => this.successMessage());
    currentExtensionMessageDate = computed(() => this.extensionMessageDate());
    currentSurvey = computed(() => this.survey());
    currentNewDueDate = computed(() => this.newDueDate());
    currentMostRecentDueDate = computed(() => this.mostRecentDueDate());
    currentExtendedDueDate = computed(() => this.extendedDueDate());
    currentMinExtendedDueDate = computed(() => this.minExtendedDueDate());
    currentMaxExtendedDueDate = computed(() => this.maxExtendedDueDate());
    currentOriginalDueDate = computed(() => this.originalDueDate());
    currentEndDate = computed(() => this.endDate());
    currentExtensionError = computed(() => this.extensionError());
    currentToday = computed(() => this.today());
    currentDateError = computed(() => this.dateError());

    cpassEnabled = ecorEnv.cpassEnabled;

    constructor() {
        super();
        this.matDialogRef.disableClose = true;
        this.userStore.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(user => {
                this.requestUser.set(user);
            });
    }

    formGroup = new FormGroup({
        extendedDueDate: new FormControl({ value: '', disabled: false }, [Validators.required])
    });


    confirm() {
        this.requestExtensionButtonDisabled.set(true);
        const extendedDueDate = this.formGroup.get('extendedDueDate').value;
        const currentSurvey = this.currentSurvey();
        const currentUser = this.currentUser();
        const currentOriginalDueDate = this.currentOriginalDueDate();

        if (currentSurvey && currentUser && currentOriginalDueDate) {
            let date = new Date(extendedDueDate);
            date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), currentOriginalDueDate.getHours(), currentOriginalDueDate.getMinutes(), currentOriginalDueDate.getSeconds(), 0);
            date.setUTCFullYear(date.getFullYear(), date.getMonth(), date.getDate());
            this.requestParadataEventService.add(ParadataEventEnum.TimeExtension, currentSurvey.surveyId);

            this.requestExtensionDialogHttpService.requestExtension(date, currentSurvey.surveyInstanceId, currentUser.userAccountId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (response) => {
                        this.success.set(true);
                        this.successMessage.set('The due date for the ' + currentSurvey.displayName + ' has been changed to ');
                        this.extensionMessageDate.set(new Date(response.mostRecentSurveyExtendedDate));
                        this.newDueDate.set(new Date(response.mostRecentSurveyExtendedDate));
                        this.requestExtensionButtonDisabled.set(false);
                    },
                    error: (error: HttpErrorResponse) => {
                        this.success.set(false);

                        if (error.error.message === 'EC-116') {
                            this.extensionError.set('Extended Due Date must be after Current Due Date');
                        } else if (error.error.message === 'EC-145') {
                            this.extensionError.set('Extended Due Date must not be after Schedule End Date');
                        } else if (error.error.message === 'EC-146') {
                            this.extensionError.set('Extended Due Date must be after most recent Extension Date');
                        }

                        this.success.set(false);
                        this.requestExtensionButtonDisabled.set(false);
                    }
                });
        }
    }

    ngOnInit() {  
        const today = new Date();
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        this.today.set(todayDate);
        this.dateError.set('');

        // Add null check for requestExtensionDialogData
        if (!this.requestExtensionDialogData) {
            console.error('RequestExtensionDialogData is undefined');
            return;
        }

        // Add null check for survey
        if (!this.requestExtensionDialogData.survey) {
            console.error('Survey data is undefined in RequestExtensionDialogData');
            return;
        }

        this.survey.set(this.requestExtensionDialogData.survey);
        
        const currentSurvey = this.currentSurvey();
        if (currentSurvey) {
            this.mostRecentDueDate.set(new Date(currentSurvey.currentDueDate)); //Equals either the original dueDate or the most recent extendedDueDate
            this.extendedDueDate.set(new Date(currentSurvey.currentDueDate)); //Value that will be submitted to server as new Extension Date (picking date on calendar updates this)
            this.minExtendedDueDate.set(new Date(currentSurvey.currentDueDate)); //Earliest date a user can extend to
            this.originalDueDate.set(new Date(currentSurvey.originalDueDate)); //Original due date set on initial load
            this.endDate.set(new Date(currentSurvey.endDate)); //Last possible date for this survey
            this.successMessage.set('');
            this.extensionMessageDate.set(null);
            this.extensionError.set('');
            this.success.set(false);

            const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
            const currentMinExtendedDueDate = this.currentMinExtendedDueDate();
            const currentToday = this.currentToday();
            const currentEndDate = this.currentEndDate();
            const currentOriginalDueDate = this.currentOriginalDueDate();

            if (currentMinExtendedDueDate && currentToday && currentMinExtendedDueDate < currentToday) {
                this.minExtendedDueDate.set(currentToday);
            }
            const updatedMinDate = this.currentMinExtendedDueDate();
            if (updatedMinDate) {
                this.minExtendedDueDate.set(new Date(updatedMinDate.getTime() + DAY_MILLISECONDS));
            }

            const finalMinDate = this.currentMinExtendedDueDate();
            if (currentEndDate && finalMinDate && currentEndDate < finalMinDate) {
                this.dateError.set('You have been granted the current time extension limit. At this time, no future extension is available.');
            }

            // If minExtendedDueDate + extensionWindow > endDate, set max to endDate
            this.maxExtendedDueDate.set(currentEndDate);
            if (currentSurvey.extensionWindow && parseInt(currentSurvey.extensionWindow)) {
                const maxDate = new Date(Math.max(currentOriginalDueDate?.getTime() || 0, new Date().getTime()));
                maxDate.setDate(maxDate.getDate() + parseInt(currentSurvey.extensionWindow));
                const finalMaxDate = new Date(Math.min(maxDate.getTime(), currentEndDate?.getTime() || 0));
                this.maxExtendedDueDate.set(finalMaxDate);

                const finalMin = this.currentMinExtendedDueDate();
                const finalMax = this.currentMaxExtendedDueDate();
                if (finalMin && finalMax && finalMin.getTime() > finalMax.getTime()) {
                    this.minExtendedDueDate.set(finalMax);
                }
            }

            if (currentEndDate && currentSurvey.currentDueDate > currentEndDate) {
                this.dateError.set('You have been granted the current time extension limit. At this time, no future extension is available.');
            }
        
            /*
                Today is before the initial due date but before the survey end date - extension window
                    Max date = initial due date + extension window
                Today is before the initial due date but after the survey end date + extension window
                    Max date = end date
        
                Today is after the initial due date but before the survey end date - extension window
                    Max date = current date + extension window
                Today is after the initial due date but after the survey end date + extension window
                    Max date = end date
            */

            if (this.currentDateError() === '') {
                this.formGroup.get('extendedDueDate').setErrors({ 'incorrect': false });
                this.formGroup.get('extendedDueDate').enable();
            } else {
                this.formGroup.get('extendedDueDate').setErrors({ 'incorrect': true });
                this.formGroup.get('extendedDueDate').disable();
            }
        }
    }

    exportSurvey() {
        const currentSurvey = this.currentSurvey();
        if (currentSurvey) {
            this.generateCalendar([], currentSurvey.surveyInstanceId);
        }
    }

    cancel() {
        this.close('cancel');
    }

    ok() {
        this.close('success');
    }

    close(value: any) {
        this.matDialogRef.close(value);
    }

    @HostListener('keydown.esc')
    public onEsc() {
        this.close('ESC keydown');
    }

    ngOnDestroy() {
        this.requestParadataEventService.flushParadataevents();
    }
}
